/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import * as React from "react"
import { X, ChevronDown, Loader2 } from "lucide-react"
import { type PlanDisplayData } from "@/lib/plan-display-data"
import { cn } from "@/lib/utils"
import {
  useVisitorQuotesStore,
  type TierAdjustmentFactor,
  type TierOptionalBenefit,
  type TpaConfig,
} from "@/stores/visitor-quotes"
import { OptionalBenefitToggle } from "./OptionalBenefitToggle"

interface ConfigureBenefitsModalProps {
  plan: PlanDisplayData
  configValues: Record<string, string>
  onSave: (values: Record<string, string>) => void
  onClose: () => void
}

const FACTOR_LABEL_OVERRIDES: Record<string, string> = {
  aml: "Annual Medical Limit",
  pec: "Pre-existing & Chronic Conditions",
}

export function ConfigureBenefitsModal({
  plan,
  onSave,
  onClose,
}: ConfigureBenefitsModalProps) {
  const getTierOptions = useVisitorQuotesStore((s) => s.getTierOptions)
  const updateQuoteSelections = useVisitorQuotesStore(
    (s) => s.updateQuoteSelections
  )
  const fetchGeneratedQuotesFromCookie = useVisitorQuotesStore(
    (s) => s.fetchGeneratedQuotesFromCookie
  )

  const [isLoadingOptions, setIsLoadingOptions] = React.useState(false)
  const [adjustmentFactors, setAdjustmentFactors] = React.useState<
    TierAdjustmentFactor[]
  >([])
  const [optionalBenefits, setOptionalBenefits] = React.useState<
    TierOptionalBenefit[]
  >([])
  // One selection per benefit_type (e.g. dental → chosen benefit row id).
  // Group by benefitType so the UI shows one toggle card per type.
  const [selectedByType, setSelectedByType] = React.useState<
    Record<string, string>
  >({})

  // selectedIds: factorType -> option id
  const [selectedIds, setSelectedIds] = React.useState<Record<string, string>>(
    {}
  )
  const [isSaving, setIsSaving] = React.useState(false)

  // TPA state
  const [selectedTpa, setSelectedTpa] = React.useState<string | null>(null)
  const [tpaConfig, setTpaConfig] = React.useState<TpaConfig | null>(null)

  // US/CA toggle (IPMI only)
  const [usaCanadaIncluded, setUsaCanadaIncluded] =
    React.useState<boolean>(false)
  const [productType, setProductType] = React.useState<string | null>(null)
  const [productCode, setProductCode] = React.useState<string | null>(null)

  // Group/Individual selector (Micro SME only)
  const [pricingType, setPricingType] = React.useState<"group" | "individual">(
    "group"
  )
  // OP network upgrade: standard (OP=WN), full (OP=same as IP), or alt (Premier-only: OP=RN).
  // Micro SME only.
  const [opNetwork, setOpNetwork] = React.useState<"standard" | "full" | "alt">(
    "standard"
  )

  // Fetch fresh options from API on mount
  React.useEffect(() => {
    let cancelled = false

    async function fetchOptions() {
      if (!plan.productCode || !plan.planTierId) {
        const fallback = (plan.adjustmentFactors ??
          []) as unknown as TierAdjustmentFactor[]
        if (cancelled) return
        setAdjustmentFactors(fallback)
        const init: Record<string, string> = {}
        fallback.forEach((af) => {
          const def =
            af.options.find((o) => o.isDefault)?.id ?? af.options[0]?.id
          if (def) init[af.factorType] = def
        })
        setSelectedIds(init)
        return
      }
      if (!cancelled) setIsLoadingOptions(true)
      const data = await getTierOptions(plan.productCode, plan.planTierId, plan.visaEmirate)
      if (cancelled) return
      setIsLoadingOptions(false)
      const factorsByType = data?.factorsByType ?? {}
      const factors: TierAdjustmentFactor[] = Object.entries(factorsByType).map(
        ([factorType, options]) => ({ factorType, required: true, options })
      )
      setAdjustmentFactors(factors)
      const init: Record<string, string> = {}
      factors.forEach((af) => {
        const currentlySelected = plan.adjustmentFactors?.find(
          (paf) => paf.factorType === af.factorType
        )?.selectedId
        // Only honour the saved selection if it exists in the (possibly
        // emirate-filtered) options list; otherwise the saved value belongs
        // to a different emirate bucket and we should use the emirate default.
        const savedInList = currentlySelected
          ? af.options.find((o) => o.id === currentlySelected)
          : undefined
        const def =
          savedInList?.id ??
          af.options.find((o) => o.isDefault)?.id ??
          af.options[0]?.id
        if (def) init[af.factorType] = def
      })
      setSelectedIds(init)
      // Optional benefits — group by benefit_type so each shows as one toggle.
      // Hydration must mirror the quote's actual saved state so that opening
      // the modal and immediately saving (without edits) leaves the price
      // unchanged. New quotes are auto-populated with defaults at generation
      // time (see buildOptionalBenefits in quote-generation.service); the
      // modal just reflects what's there.
      const benefits = data?.optionalBenefits ?? []
      setOptionalBenefits(benefits)
      const defaultByType: Record<string, string> = {}
      const byType = new Map<string, TierOptionalBenefit[]>()
      for (const b of benefits) {
        if (!byType.has(b.benefitType)) byType.set(b.benefitType, [])
        byType.get(b.benefitType)!.push(b)
      }
      // `plan.optionalBenefits` from the GET response is shaped
      // [{benefitType, selectedId, options}] — selectedId reflects what the
      // quote actually has stored. Map type → selectedId for fast lookup.
      const savedSelectionByType = new Map<string, string | null | undefined>()
      const savedBenefits =
        (
          plan as unknown as {
            optionalBenefits?: Array<{
              benefitType?: string
              selectedId?: string | null
            }>
          }
        ).optionalBenefits ?? []
      for (const b of savedBenefits) {
        if (b.benefitType) savedSelectionByType.set(b.benefitType, b.selectedId)
      }
      // All benefit types are always enabled — no toggle.
      for (const [type, rows] of byType) {
        const savedId = savedSelectionByType.get(type) ?? null
        const previouslyPicked = savedId
          ? rows.find((r) => r.id === savedId)
          : undefined
        // Pick what the server-side visitor-quote generator would pick:
        // the cheapest row in the type group (mirrors the
        // `orderBy: rate ASC` in buildOptionalBenefits).
        const cheapest = [...rows].sort((a, b) => a.rate - b.rate)[0]
        const preSelected =
          previouslyPicked ??
          rows.find((r) => r.isDefault) ??
          cheapest ??
          rows[0]
        defaultByType[type] = preSelected.id
      }
      setSelectedByType(defaultByType)

      // Pull the saved snapshot off the plan — it carries the user's last choices.
      const snap = (
        plan as unknown as {
          ratesSnapshot?: {
            tpa_selected?: string | null
            usa_canada_included?: boolean | null
            beneficiary_group?: "principal" | "dependent" | null
            pricing_type?: string | null
            pricingType?: string | null
          }
        }
      ).ratesSnapshot

      // TPA — prefer persisted selection, then product default.
      if (data?.product?.tpaConfig) {
        setTpaConfig(data.product.tpaConfig as TpaConfig)
      }

      const isSme =
        data?.product?.type === "micro_sme" ||
        data?.product?.type === "sme" ||
        data?.product?.code?.includes("sme");

      if (isSme && (data?.product?.tpaConfig as TpaConfig)?.tpas?.some(t => t.name.toUpperCase() === "NAS")) {
        setSelectedTpa("NAS")
      } else if (snap?.tpa_selected) {
        setSelectedTpa(snap.tpa_selected)
      } else if (plan.tpa) {
        setSelectedTpa(plan.tpa)
      } else if (data?.product?.defaultTpa) {
        setSelectedTpa(data.product.defaultTpa)
      }

      if (data?.product?.type) setProductType(data.product.type)
      if (data?.product?.code) setProductCode(data.product.code)

      // IPMI US/CA — prefer persisted choice, then the tier's flag.
      if (snap?.usa_canada_included != null) {
        setUsaCanadaIncluded(!!snap.usa_canada_included)
      } else if (data?.tier?.usaCanadaIncluded != null) {
        setUsaCanadaIncluded(data.tier.usaCanadaIncluded)
      }

      // Hydrate current pricingType from ratesSnapshot if present on the plan.
      // Snapshot can be any of group/individual/group_upgraded/individual_upgraded — split it.
      const persistedPricingType =
        snap?.pricing_type ?? snap?.pricingType
      if (typeof persistedPricingType === "string") {
        const isAlt = persistedPricingType.endsWith("_upgraded_alt")
        const isUpgraded = !isAlt && persistedPricingType.endsWith("_upgraded")
        const base = isAlt
          ? persistedPricingType.replace("_upgraded_alt", "")
          : isUpgraded
            ? persistedPricingType.replace("_upgraded", "")
            : persistedPricingType
        if (base === "group" || base === "individual") setPricingType(base)
        setOpNetwork(isAlt ? "alt" : isUpgraded ? "full" : "standard")
      }
    }

    fetchOptions()
    return () => {
      cancelled = true
    }
  }, [])
  function handleChange(factorType: string, optionId: string) {
    setSelectedIds((prev) => ({ ...prev, [factorType]: optionId }))
  }

  async function handleSave() {
    setIsSaving(true)
    const adjustmentFactorIds = adjustmentFactors
      .map((af) => selectedIds[af.factorType])
      .filter(Boolean) as string[]

    // Send one optional benefit id per type (all types are always included).
    const optionalBenefitIds = Object.entries(selectedByType)
      .filter(([, id]) => id)
      .map(([, id]) => id)

    const ok = await updateQuoteSelections(plan.slug, {
      adjustmentFactorIds,
      optionalBenefitIds,
      ...(selectedTpa ? { tpa: selectedTpa } : {}),
      ...(plan.benefitCategory ? { benefitCategory: plan.benefitCategory } : {}),
      ...(productType === "ipmi" ? { usaCanadaIncluded } : {}),
      ...(productType === "micro_sme"
        ? {
            pricingType: (opNetwork === "full"
              ? `${pricingType}_upgraded`
              : opNetwork === "alt"
                ? `${pricingType}_upgraded_alt`
                : pricingType) as
              | "group"
              | "individual"
              | "group_upgraded"
              | "individual_upgraded"
              | "group_upgraded_alt"
              | "individual_upgraded_alt",
          }
        : {}),
    })

    setIsSaving(false)
    if (ok) {
      // Re-fetch the visitor-quotes list from the API so every plan card
      // reflects the updated ratesSnapshot from the server.
      // GET /api/v1/visitor-quotes/{visitorId}/quotes
      await fetchGeneratedQuotesFromCookie(
        (plan as any).benefitCategory ?? undefined
      )
      onSave({})
      onClose()
    }
  }

  function setTypeSelection(type: string, id: string) {
    setSelectedByType((prev) => ({ ...prev, [type]: id }))
  }

  const benefitsByType = React.useMemo(() => {
    const map = new Map<string, TierOptionalBenefit[]>()
    for (const b of optionalBenefits) {
      if (!map.has(b.benefitType)) map.set(b.benefitType, [])
      map.get(b.benefitType)!.push(b)
    }
    return map
  }, [optionalBenefits])

  const hasAdjustments = adjustmentFactors.length > 0
  const hasOptionalBenefits = optionalBenefits.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 max-h-[80vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <p className="text-sm font-semibold text-nova-600 dark:text-nova-400">
            Configure Benefits
          </p>
          <h2 className="mt-0.5 text-2xl font-bold text-slate-900 dark:text-white">
            {plan.name}
          </h2>
          {(productType === "ipmi" ||
            plan.productCode === "ipmi_indv" ||
            plan.productCode === "ipmi_sme") && (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              All benefit rates and limits are displayed in USD.
            </p>
          )}
        </div>

        {/* Fields */}
        <div className="space-y-4 px-6 pb-6">
          {isLoadingOptions ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-nova-600" />
            </div>
          ) : !hasAdjustments &&
            productType !== "ipmi" &&
            productCode !== "sme_26_200" &&
            productType !== "micro_sme" ? (
            <div className="py-8 text-center text-sm text-slate-500">
              No configurable options available for this plan.
            </div>
          ) : (
            <>
              {tpaConfig && tpaConfig.tpas.length > 1 && (
                <div className="mb-4">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    TPA Provider
                  </label>
                  <div className="relative">
                    <select
                      value={selectedTpa ?? ""}
                      onChange={(e) => setSelectedTpa(e.target.value)}
                      className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-800 focus:border-nova-400 focus:ring-2 focus:ring-nova-200 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    >
                      {tpaConfig.tpas
                        .filter((t) => {
                          const isSme =
                            productType === "micro_sme" ||
                            productType === "sme" ||
                            productCode?.includes("sme");
                          if (isSme) {
                            return t.name.toUpperCase() === "NAS";
                          }
                          return true;
                        })
                        .map((t) => (
                          <option key={t.name} value={t.name}>
                            {t.name}
                          </option>
                        ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Network labels reflect the selected TPA
                  </p>
                </div>
              )}
              {/* {productType === "micro_sme" && (
                <div className="mb-4">
                  <p className="mb-2 text-sm font-medium">Pricing Type</p>
                  <div className="flex gap-2">
                    {(["group", "individual"] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setPricingType(type)}
                        className={cn(
                          "flex-1 cursor-pointer rounded-xl border px-3 py-2 text-sm font-medium transition-colors",
                          pricingType === type
                            ? "border-nova-600 bg-nova-600 text-white"
                            : "border-slate-300 bg-white text-slate-600 hover:border-nova-400 dark:bg-slate-800 dark:text-slate-300"
                        )}
                      >
                        {type === "group" ? "Group" : "Individual"}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Group rates apply to employer-sponsored plans; Individual
                    rates apply to self-purchased policies.
                  </p>
                </div>
              )} */}
              {productType === "micro_sme" &&
                (() => {
                  // Per-TPA OP network labels. Pricing is TPA-agnostic — these
                  // strings only re-label the OP network on the UI/PDF when the
                  // user picks NAS vs Nextcare vs Mednet. Source: client spec.
                  type Op = "standard" | "full" | "alt"
                  const OP_LABELS: Record<
                    string,
                    Record<string, Partial<Record<Op, string>>>
                  > = {
                    "Essential Care": {
                      NAS: { standard: "WN" },
                      Nextcare: { standard: "RN3" },
                      Mednet: { standard: "Pearl" },
                    },
                    "Core Care": {
                      NAS: { standard: "WN", full: "SRN" },
                      Nextcare: { standard: "RN3", full: "RN2" },
                      Mednet: { standard: "Pearl", full: "Green" },
                    },
                    "Enhanced Care": {
                      NAS: { standard: "WN", full: "RN" },
                      Nextcare: { standard: "RN3", full: "RN" },
                      Mednet: { standard: "Pearl", full: "Silver Classic" },
                    },
                    "Premier Care": {
                      NAS: { standard: "WN", full: "GN", alt: "RN" },
                      Nextcare: { standard: "RN3", full: "GN", alt: "RN" },
                      Mednet: {
                        standard: "Pearl",
                        full: "Silver Premium",
                        alt: "Silver Classic",
                      },
                    },
                    "Elite Care": {
                      NAS: { standard: "WN", full: "CN" },
                      Nextcare: { standard: "RN3", full: "GN+" },
                      Mednet: { standard: "Pearl", full: "Gold" },
                    },
                  }
                  const tpaLabels =
                    OP_LABELS[plan.name]?.[selectedTpa ?? "NAS"] ?? {}
                  const availableOps: Op[] = (
                    ["standard", "full", "alt"] as Op[]
                  ).filter((o) => !!tpaLabels[o])
                  if (availableOps.length <= 1) return null // Essential: nothing to choose
                  return (
                    <div className="mb-4">
                      <p className="mb-2 text-sm font-medium">
                        Outpatient Network
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {availableOps.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setOpNetwork(opt)}
                            className={cn(
                              "min-w-[120px] flex-1 cursor-pointer rounded-xl border px-3 py-2 text-sm font-medium transition-colors",
                              opNetwork === opt
                                ? "border-nova-600 bg-nova-600 text-white"
                                : "border-slate-300 bg-white text-slate-600 hover:border-nova-400 dark:bg-slate-800 dark:text-slate-300"
                            )}
                          >
                            {opt === "standard"
                              ? "Standard"
                              : opt === "full"
                                ? "Full Network"
                                : "Alt Network"}
                            <span className="ml-1 text-xs opacity-80">
                              ({tpaLabels[opt]})
                            </span>
                          </button>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        Standard keeps OP at the entry-level network (
                        {tpaLabels.standard}); Full upgrades OP to match the
                        inpatient tier ({tpaLabels.full}).
                        {tpaLabels.alt
                          ? ` Premier Care offers a third combo (${tpaLabels.alt}) with its own rate.`
                          : ""}
                      </p>
                    </div>
                  )
                })()}
              {/*
                Beneficiary Group is intentionally not rendered: SME 26-200 is
                priced per-census-member. Each row's relationship + visa
                emirate (from the CSV) drives the rate pick, so a single
                quote-wide toggle would silently misprice the whole group.
              */}
              {adjustmentFactors.map((af) => (
                <div key={af.factorType}>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {FACTOR_LABEL_OVERRIDES[af.factorType] ??
                      af.factorType
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </label>
                  <div className="relative">
                    <select
                      value={selectedIds[af.factorType] ?? ""}
                      onChange={(e) =>
                        handleChange(af.factorType, e.target.value)
                      }
                      className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-800 focus:border-nova-400 focus:ring-2 focus:ring-nova-200 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    >
                      {af.options.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.optionLabel}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>
              ))}
              {hasOptionalBenefits && (
                <div className="border-t border-slate-100 pt-4 dark:border-slate-700">
                  <p className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Optional Benefits
                  </p>
                  <p className="mb-3 text-xs text-slate-500">
                    All benefits are included; pick your preferred options.
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {Array.from(benefitsByType.entries())
                      .filter(
                        ([type]) =>
                          type !== "territory" || productType !== "ipmi"
                      )
                      .map(([type, rows]) => (
                        <OptionalBenefitToggle
                          key={type}
                          benefitType={type}
                          options={rows}
                          selectedBenefitId={selectedByType[type] ?? null}
                          onSelectOption={(id) => setTypeSelection(type, id)}
                        />
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-slate-100 px-6 py-4 dark:border-slate-700">
          <button
            onClick={handleSave}
            disabled={
              isSaving ||
              isLoadingOptions ||
              (!hasAdjustments &&
                productType !== "ipmi" &&
                productCode !== "sme_26_200" &&
                productType !== "micro_sme")
            }
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-full bg-nova-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-nova-700 disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            Update Pricing
          </button>
          <button
            onClick={onClose}
            className="cursor-pointer rounded-full border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
